import { type FC } from "react";
import { Button, Stack } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { Filter, FilterProvider } from "@/partner/shared/components/Filter";
import { EmailTemplatesList } from "./EmailTemplatesList";
import { EmailTemplateDialog } from "./EmailTemplateDialog";

/**
 * Библиотека писем (спека marketing-automations, фаза 1), устроена как
 * библиотека опросов (PollsLibrary): поиск внутри панели фильтра, редактор
 * письма fullScreen-диалогом по URL (?tab=create, ?tab=edit&id=), чтобы на
 * письмо можно было дать ссылку.
 */
const EmailTemplatesLibraryInner: FC = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get("tab");
  const editId = searchParams.get("id");

  const openCreate = () => {
    searchParams.set("tab", "create");
    setSearchParams(searchParams);
  };

  const openEdit = (id: string) => {
    searchParams.set("tab", "edit");
    searchParams.set("id", id);
    setSearchParams(searchParams);
  };

  // Созданное письмо остаётся в диалоге, адрес меняется на ?tab=edit&id=
  // без новой записи в истории: «Назад» не должен открывать пустое письмо.
  const onCreated = (id: string) => {
    searchParams.set("tab", "edit");
    searchParams.set("id", id);
    setSearchParams(searchParams, { replace: true });
  };

  const closeDialog = () => {
    searchParams.delete("tab");
    searchParams.delete("id");
    setSearchParams(searchParams);
  };

  return (
    <>
      <Stack spacing={1}>
        <Filter
          title={t("emailTemplates.list.title")}
          action={
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              disableElevation
              onClick={openCreate}
            >
              {t("common.actions.add")}
            </Button>
          }
          fields={{ search: true }}
        />
        <EmailTemplatesList onEdit={openEdit} />
      </Stack>
      {/* Один диалог на оба режима: после создания меняется только адрес,
          диалог остаётся открытым и не проигрывает анимацию заново. */}
      {(tab === "create" || (tab === "edit" && editId)) && (
        <EmailTemplateDialog
          templateId={tab === "edit" ? editId : null}
          onClose={closeDialog}
          onCreated={onCreated}
        />
      )}
    </>
  );
};

export const EmailTemplatesLibrary: FC = () => (
  <FilterProvider>
    <EmailTemplatesLibraryInner />
  </FilterProvider>
);
